// `rekor template` — consumer surface for the Templates gallery (#535).
//
//   rekor template list                          List published templates
//   rekor template pull <slug> [--lang <l>]      Write a template's Rekor data layer into rekor-ws/
//
// Pull writes the rekor slice (collections, relationship-types, triggers, the MCP Factory toolset)
// into rekor-ws/databases/<id>/ as a fresh preview-bound folder, then prints the create→push→promote
// next steps. Authoring lives in the platform-admin `rekor admin template push`. The read API
// (`GET /templates[...]`) is identity-gated, so these commands require login: `ApiClient` attaches
// the bearer when present, and an anonymous call 401s → the shared error map prints "Run `rekor login`".
import { Command } from 'commander';
import chalk from 'chalk';
import { relative } from 'path';
import { ApiClient } from '../client.js';
import { ApiError } from '../errors.js';
import { formatOutput } from '../output.js';
import { getFormat } from '../helpers.js';
import { detectWorkspace, DATABASE_META_FILE } from '../config-as-code/workspace.js';
import { folderForSelector } from '../config-as-code/workspace.js';
import { parseYaml, stringifyYaml } from '../config-as-code/yaml.js';
import { writeFileMap, findFileMapCollisions, FileCollisionError } from '../templates/files.js';
import { TEMPLATE_SLUG_REGEX, isTemplateLocale } from '../templates/types.js';
import type { TemplateListResponse, TemplateDetail, TemplateLocale } from '../templates/types.js';
import type { DatabaseMeta } from '../config-as-code/types.js';

export const templateCommand = new Command('template')
  .description('Browse and pull ready-made data-layer templates');

templateCommand.command('list')
  .description('List available templates')
  .action(async function (this: Command) {
    const client = new ApiClient();
    const data = await client.request<TemplateListResponse>('GET', '/templates');
    const templates = data.templates ?? [];
    if (getFormat(this) === 'json') {
      console.log(JSON.stringify(templates, null, 2));
      return;
    }
    if (templates.length === 0) {
      console.log('(no templates published yet)');
      return;
    }
    const rows = templates.map((t) => ({
      slug: t.slug,
      category: t.category,
      includes: includesLabel(t),
    }));
    console.log(formatOutput(rows, 'table'));
  });

templateCommand.command('pull <slug>')
  .description("Pull a template's Rekor data layer into rekor-ws/databases/<id>/ (id defaults to the slug; override with the global --database)")
  .option('--lang <locale>', 'Localized variant (en|pt|es); defaults to the template default')
  .option('--force', 'Overwrite existing files in the target dir (default: refuse on any collision)')
  .option('--dry-run', 'Preview the files that would be written, then exit without writing')
  .action(async function (this: Command, slug: string, opts: { lang?: string; force?: boolean; dryRun?: boolean }) {
    if (!TEMPLATE_SLUG_REGEX.test(slug)) {
      console.error(`Invalid template slug: ${slug}`);
      process.exit(1);
    }
    let lang: TemplateLocale | undefined;
    if (opts.lang !== undefined) {
      if (!isTemplateLocale(opts.lang)) {
        console.error(`Invalid --lang "${opts.lang}": must be one of en, pt, es.`);
        process.exit(1);
      }
      lang = opts.lang;
    }

    const ws = detectWorkspace();
    if (!ws) {
      console.error('Not inside a git repository — rekor-ws lives at the repo root.');
      process.exit(1);
    }

    // Target db id = global --database, else the slug. The artifact is account-agnostic; the
    // origin pointer is injected below so the existing `rekor push` flow can create the preview.
    const targetId = (this.optsWithGlobals()['database'] as string | undefined) ?? slug;

    const client = new ApiClient();
    let detail: TemplateDetail;
    try {
      const qs = lang ? `?locale=${lang}` : '';
      detail = await client.request<TemplateDetail>('GET', `/templates/${encodeURIComponent(slug)}${qs}`);
    } catch (err) {
      exitOnTemplateError(err);
      return;
    }

    if (detail.resolvedLocale !== detail.requestedLocale) {
      console.log(`Note: "${detail.requestedLocale}" not translated yet — pulled "${detail.resolvedLocale}" instead.`);
    }

    if (!detail.includes.rekor || Object.keys(detail.files).length === 0) {
      console.log(`Template "${slug}" has no Rekor data layer to pull.`);
      return;
    }

    const targetDir = folderForSelector(ws, targetId);

    // Inject the origin pointer so the pulled folder is `rekor push`-able as a fresh preview. The
    // slice ships database.yaml id-free (portable); we set origin_database_id = the chosen id and
    // never carry a database_id (that would be a foreign id in someone else's account).
    const files = { ...detail.files };
    const sliceMeta = (files[DATABASE_META_FILE] ? parseYaml(files[DATABASE_META_FILE]) : null) as DatabaseMeta | null;
    const meta: DatabaseMeta = {
      ...(sliceMeta ?? {}),
      name: sliceMeta?.name ?? targetId,
      environment: 'preview',
      origin_database_id: targetId,
    };
    delete meta.database_id;
    files[DATABASE_META_FILE] = stringifyYaml(meta);

    const rel = relative(ws.gitRoot, targetDir);

    if (opts.dryRun) {
      // The write path gets its collision set from writeFileMap's own check (via the thrown
      // FileCollisionError), so only the preview needs to compute it here.
      const collisions = findFileMapCollisions(targetDir, files);
      console.log(`Dry run — would write ${Object.keys(files).length} file(s) to ${rel}/:`);
      for (const f of Object.keys(files).sort()) {
        const mark = collisions.includes(f) ? chalk.yellow(' (overwrites existing)') : '';
        console.log(`  ${f}${mark}`);
      }
      if (collisions.length > 0) {
        console.log(
          chalk.yellow(
            `\n${collisions.length} existing file(s) would be overwritten. Re-run with --force to proceed, or --database <new-id> to write elsewhere.`,
          ),
        );
      }
      return;
    }

    let written: string[];
    try {
      written = writeFileMap(targetDir, files, { overwrite: opts.force });
    } catch (err) {
      if (err instanceof FileCollisionError) {
        console.error(`Refusing to overwrite ${err.collisions.length} existing file(s) in ${rel}/:`);
        for (const f of err.collisions) console.error(`  ${f}`);
        console.error(
          '\nRe-run with --force to overwrite, --database <new-id> to write to a different folder, or --dry-run to preview.',
        );
        process.exit(1);
      }
      throw err;
    }
    console.log(chalk.green(`Pulled template "${slug}" → ${rel}/ (${written.length} file${written.length === 1 ? '' : 's'}).`));
    console.log('\nNext — stand it up as your own database:');
    console.log(`  rekor databases create ${targetId} --name ${JSON.stringify(meta.name)}`);
    console.log(`  rekor push ${targetId}`);
    console.log(`  rekor databases promote ${targetId} --from <preview>`);
    console.log(chalk.dim('\nOnce promoted, its MCP toolset(s) go live — see the toolsets in the pulled slice.'));
  });

function includesLabel(t: { includes: { rekor: boolean } }): string {
  return t.includes.rekor ? 'rekor data layer' : '(none)';
}

function exitOnTemplateError(err: unknown): never {
  if (err instanceof ApiError) {
    if (err.status === 401) console.error('Authentication failed. Run `rekor login` again.');
    else if (err.status === 404) console.error('Template not found. Run `rekor template list` to see available templates.');
    else if (err.status === 429) console.error('Rate limited. Please retry in a moment.');
    else console.error(err.message);
    process.exit(1);
  }
  console.error(err instanceof Error ? err.message : String(err));
  process.exit(1);
}
